// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import React, { FC, useCallback } from "react";
/* eslint-disable spire/fenced-imports */
import { useSwiper } from "swiper/react";
/* eslint-enable spire/fenced-imports */

import { useArrowElement } from "@turnerBlueprint/App/Components/XngCarousel/hooks";
import { ArrowPosition, ArrowType, NavigationProps } from "@turnerBlueprint/App/Components/XngCarousel/models";
import IconPaginationArrowLeft from "@turnerBlueprint/XDL/Icons/IconPaginationLeft";
import IconPaginationArrowRight from "@turnerBlueprint/XDL/Icons/IconPaginationRight";

import { color, xClassNames } from "@turnerBlueprint/XDL/Utilities";

import { ButtonIcon } from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import { IconWrapper } from "@insite/mobius/Icon";
import ArrowDown from "@insite/mobius/Icons/ArrowDown";
import ArrowUp from "@insite/mobius/Icons/ArrowUp";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

const generateArrowStyle = (arrowPosition: ArrowPosition, showOnMobile: boolean) => {
    return {
        css: css`
            ${!showOnMobile
                ? css`
                      position: absolute;
                      ${arrowPosition === "left" ? `left: 0;` : `right: 0;`};
                      top: 50%;
                      background: ${color("common.ui6", 0.75)};
                      height: 78px;
                      width: 34px;
                      transform: translate(0, -50%);
                      & > ${IconWrapper} {
                          color: ${color("common.white")};
                      }
                  `
                : css`
                      ${arrowPosition === "left" ? "margin-right: 16px;" : "margin-left: 16px;"}
                      & > ${IconWrapper} {
                          color: ${color("common.ui4")};
                      }
                  `}
            & > ${IconWrapper} {
                transition: all ${getProp("theme.transition.duration.regular", "300")}ms ease-in-out;
                &,
                > svg {
                    width: auto;
                    height: 22px;
                }
            }
            &:not(.swiper-button-disabled):hover {
                & > ${IconWrapper} {
                    color: ${color("common.ui9")};
                }
            }
            z-index: 10;
            padding: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            border: 0;
            outline: none !important;

            &:focus {
                box-shadow: none;
            }
            &.swiper-button-disabled {
                cursor: not-allowed;
                display: ${showOnMobile ? "" : "none"}; // hide the navigation if its disabled
                & > ${IconWrapper} {
                    color: ${color("common.ui12", 0.33)};
                }
            }
        `,
    };
};

const arrowNavigationIcons = {
    top: {
        src: ArrowUp,
        size: 14,
    },
    bottom: {
        src: ArrowDown,
        size: 14,
    },
    left: {
        src: IconPaginationArrowLeft,
        size: 14,
    },
    right: {
        src: IconPaginationArrowRight,
        size: 14,
    },
};

const getArrowPosition = (arrowType: ArrowType, direction: "horizontal" | "vertical" = "horizontal"): ArrowPosition => {
    const isVertical = direction === "vertical";
    if (arrowType === "next") {
        return isVertical ? "bottom" : "right";
    }
    return isVertical ? "top" : "left";
};

const ArrowNavigation: FC<NavigationProps> = React.memo(
    ({ arrowType, showOnMobile, buttonIconProps, scrollbar, ...otherProps }) => {
        const swiperInstance = useSwiper();
        const arrowPosition = getArrowPosition(arrowType, swiperInstance?.isHorizontal() ? "horizontal" : "vertical");
        const { src: iconSrc, size: iconSize } = arrowNavigationIcons[arrowPosition];

        const {
            className,
            element: arrowElement,
            disabled,
        } = useArrowElement({
            swiper: swiperInstance,
            showOnMobile,
            arrowType,
        });

        const onClickHandler = useCallback(
            (event: any) => {
                arrowElement?.click();
                otherProps.onClick?.(event);
            },
            [arrowElement],
        );

        if (!swiperInstance || !arrowElement) {
            return null;
        }

        const props = {
            arrowPosition,
            showOnMobile,
            className: xClassNames.append(className, otherProps.className),
        };

        return (
            <Clickable
                {...props}
                {...generateArrowStyle(arrowPosition, showOnMobile)}
                aria-label={`carousel-${arrowPosition}`}
                onClick={onClickHandler}
            >
                <ButtonIcon
                    src={iconSrc}
                    size={scrollbar ? 22 : iconSize}
                    color="common.white"
                    className={`carousel-${arrowPosition}-icon ${disabled && "disabled"}`}
                    {...buttonIconProps}
                />
            </Clickable>
        );
    },
);

export default ArrowNavigation;
